import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PiPlusCircle, PiTrash, PiPencil, PiArrowLeft } from 'react-icons/pi';
import { noteService } from '../services/noteService';

const NoteFolder = () => {
  const { folderId } = useParams();
  const navigate = useNavigate();
  const [folder, setFolder] = useState(null);
  const [notes, setNotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFolderData();
  }, [folderId]);

  const loadFolderData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const folderResponse = await noteService.getFolderById(folderId);
      setFolder(folderResponse.data);
      const notesResponse = await noteService.getNotesByFolder(folderId);
      setNotes(Array.isArray(notesResponse.data) ? notesResponse.data : []);
    } catch (error) {
      console.error('Error loading folder data:', error);
      setError('Failed to load folder data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNote = async () => {
    try {
      const newNote = await noteService.createNote(folderId, {
        title: 'New Note',
        content: '# New Note\nStart writing here...'
      });
      navigate(`/notes/folder/${folderId}/note/${newNote.data._id}`);
    } catch (error) {
      console.error('Error creating note:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await noteService.deleteNote(folderId, noteId);
        loadFolderData();
      } catch (error) {
        console.error('Error deleting note:', error);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6 flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 text-red-600 p-4 rounded-lg">
            {error}
            <button 
              onClick={loadFolderData}
              className="ml-4 text-sm underline hover:no-underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center mb-6 space-x-4">
          <button
            onClick={() => navigate('/notes')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <PiArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            {folder?.name || 'Unnamed Folder'}
          </h1>
          <button
            onClick={handleCreateNote}
            className="ml-auto inline-flex items-center text-white bg-indigo-600 hover:bg-indigo-700 rounded-full p-2 transition-colors duration-200"
          >
            <PiPlusCircle className="h-5 w-5" />
          </button>
        </div>

        {/* Notes List */}
        <div className="space-y-4">
          {notes && notes.length > 0 ? (
            notes.map((note) => (
              <div
                key={note._id}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-4"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-gray-900">
                    {note.title || 'Untitled Note'}
                  </h2>
                  <div className="flex space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/notes/folder/${folderId}/note/${note._id}`);
                      }}
                      className="p-1.5 rounded-full text-indigo-600 hover:bg-indigo-50 transition-colors duration-200"
                    >
                      <PiPencil className="h-5 w-5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteNote(note._id);
                      }}
                      className="p-1.5 rounded-full text-red-600 hover:bg-red-50 transition-colors duration-200"
                    >
                      <PiTrash className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                {note.updatedAt && (
                  <p className="mt-2 text-sm text-gray-600">
                    Last modified: {new Date(note.updatedAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">No notes in this folder yet.</p>
              <button
                onClick={handleCreateNote}
                className="mt-4 text-indigo-600 hover:text-indigo-700"
              >
                Create your first note
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NoteFolder;
